import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { bolusnotesApi } from '../services/api';
import { useProfile } from '../contexts/ProfileContext';
import BolusEntryCard from '../components/BolusEntryCard';
import AddBolusEntryModal from '../components/AddBolusEntryModal';
import ProfileSelector from '../components/ProfileSelector';
import ThemeToggle from '../components/ThemeToggle';
import './BolusEntries.css';

const BolusEntries: React.FC = () => {
  const { selectedProfile } = useProfile();
  const [entries, setEntries] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<any>(null);
  const navigate = useNavigate();

  const fetchEntries = useCallback(async () => {
    if (!selectedProfile) return;
    setIsLoading(true);
    try {
      const data = await bolusnotesApi.entries.list(undefined, undefined, selectedProfile.id);
      setEntries(data);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedProfile]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleAddClick = () => {
    setEditingEntry(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (entry: any) => {
    setEditingEntry(entry);
    setIsModalOpen(true);
  };

  const handleDeleteClick = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await bolusnotesApi.entries.delete(id);
      fetchEntries();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  return (
    <div className="entries-page">
      <header className="page-header">
        <div className="header-left">
          <button onClick={() => navigate('/dashboard')} className="btn-back">← Back</button>
          <h1>💉 Bolus Entries</h1>
          <ProfileSelector />
        </div>
        <div className="header-right">
          <ThemeToggle placement="header" />
          <button onClick={handleAddClick} className="btn-add-entry">+ Add Entry</button>
        </div>
      </header>

      <div className="entries-container">
        {isLoading ? (
          <div className="loading-state">Loading doses...</div>
        ) : entries.length === 0 ? (
          <div className="empty-state">
            <p>No insulin doses tracked yet.</p>
            <button onClick={handleAddClick} className="btn-primary">Track First Dose</button>
          </div>
        ) : (
          <div className="entries-list">
            {entries.map(entry => (
              <BolusEntryCard 
                key={entry.id} 
                entry={entry} 
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
              />
            ))}
          </div>
        )}
      </div>

      <AddBolusEntryModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSave={fetchEntries}
        initialEntry={editingEntry}
      />
    </div>
  );
};

export default BolusEntries;
